import React, { useState, useEffect, useCallback } from 'react';
import { useTunnelContext } from '../TunnelContext';
import './DebugTunnelFee.css';

const formatDateForInput = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const parseHongKongDateTime = (value) => {
  if (!value || !value.includes('T')) {
    return null;
  }

  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);

  if ([year, month, day, hour, minute].some(Number.isNaN)) {
    return null;
  }

  const date = new Date(Date.UTC(year, month - 1, day, hour - 8, minute));
  return Number.isNaN(date.getTime()) ? null : date;
};

const DebugTunnelFee = () => {
  const { tunnelFees, language, debugDateTime, dispatch } = useTunnelContext();

  const initialDebugDatetime = debugDateTime ? new Date(debugDateTime) : new Date();
  const [debugState, setDebugState] = useState({
    debugDatetime: formatDateForInput(initialDebugDatetime),
    timeAcceleration: 10,
    isSimulating: false,
  });

  const [currentDebugDatetime, setCurrentDebugDatetime] = useState(initialDebugDatetime);

  const startSimulation = useCallback(() => {
    setDebugState((prev) => ({ ...prev, isSimulating: true }));
    const debugDate = parseHongKongDateTime(debugState.debugDatetime);
    if (!debugDate) return;

    setCurrentDebugDatetime(debugDate);
    dispatch({ type: 'SET_DEBUG_DATETIME', payload: debugDate.toISOString() });
  }, [debugState.debugDatetime, dispatch]);

  const stopSimulation = useCallback(() => {
    setDebugState((prev) => ({ ...prev, isSimulating: false }));
  }, []);

  const handleInputChange = useCallback((e) => {
    const { id, value } = e.target;
    if (id === 'debugDatetime') {
      setDebugState((prev) => ({ ...prev, [id]: value }));
      const debugDate = parseHongKongDateTime(value);
      if (!debugDate) return;

      setCurrentDebugDatetime(debugDate);
      dispatch({ type: 'SET_DEBUG_DATETIME', payload: debugDate.toISOString() });
    } else if (id === 'timeAcceleration') {
      setDebugState((prev) => ({ ...prev, timeAcceleration: parseInt(value, 10) }));
    }
  }, [dispatch]);

  const setToCurrentTime = useCallback(() => {
    const realNow = new Date();
    setCurrentDebugDatetime(realNow);
    setDebugState((prev) => ({
      ...prev,
      debugDatetime: formatDateForInput(realNow),
    }));
    dispatch({ type: 'SET_DEBUG_DATETIME', payload: realNow.toISOString() });
  }, [dispatch]);

  useEffect(() => {
    if (!debugDateTime) return;

    const debugDate = new Date(debugDateTime);
    if (Number.isNaN(debugDate.getTime())) return;

    setCurrentDebugDatetime(debugDate);
    setDebugState((prev) => ({
      ...prev,
      debugDatetime: formatDateForInput(debugDate),
    }));
  }, [debugDateTime]);

  useEffect(() => {
    if (!debugState.isSimulating) return;

    const intervalId = setInterval(() => {
      setCurrentDebugDatetime((prevTime) => {
        const nextTime = new Date(prevTime.getTime() + debugState.timeAcceleration * 6000);
        dispatch({ type: 'SET_DEBUG_DATETIME', payload: nextTime.toISOString() });
        return nextTime;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [debugState.isSimulating, debugState.timeAcceleration, dispatch]);

  const formattedDebugDatetime = currentDebugDatetime.toLocaleString('en-US', {
    timeZone: 'Asia/Hong_Kong',
    hour12: false,
  });

  const renderDebugTunnelFees = tunnelFees.map((tunnel) => (
    <div key={tunnel.abbr} className="debugTunnelFee-item">
      <span className="debugTunnelFee-abbr">{tunnel.abbr}</span>
      <span className="debugTunnelFee-fee">
        ${tunnel.fee?.toFixed(2) || 'N/A'}
      </span>
    </div>
  ));

  return (
    <div id="debugTunnelFee-tool">
      <div className="debugTunnelFee-header">
        <h3>{language === 'en' ? 'Time Debug Tool: Simulated Time (HKT)' : '時間除錯工具：模擬香港時間'}</h3>
      </div>
      
      <div className="debugTunnelFee-input-group">
        <div className="debugTunnelFee-input-field">
          <label htmlFor="debugDatetime">{language === 'en' ? 'Simulated Date and Time (HKT):' : '模擬日期和時間（香港時間）：'}</label>
          <input
            type="datetime-local"
            id="debugDatetime"
            value={debugState.debugDatetime}
            onChange={handleInputChange}
          />
        </div>
      </div>
      
      <div className="debugTunnelFee-input-group">
        <div className="debugTunnelFee-input-field">
          <label htmlFor="timeAcceleration">{language === 'en' ? 'Time Acceleration:' : '時間加速：'}</label>
          <input
            type="range"
            id="timeAcceleration"
            min="1"
            max="600"
            step="1"
            value={debugState.timeAcceleration}
            onChange={handleInputChange}
          />
          <span className="debugTunnelFee-acceleration-value">
            {debugState.timeAcceleration === 1
              ? '1x (real-time)'
              : `${debugState.timeAcceleration}x (${(debugState.timeAcceleration / 10).toFixed(1)} min/sec)`}
          </span>
        </div>
      </div>
      
      <div className="debugTunnelFee-button-group">
        <button onClick={startSimulation} disabled={debugState.isSimulating}>
          {language === 'en' ? 'Start Simulation' : '開始模擬'}
        </button>
        <button onClick={stopSimulation} disabled={!debugState.isSimulating}>
          {language === 'en' ? 'Stop Simulation' : '停止模擬'}
        </button>
        <button onClick={setToCurrentTime}>
          {language === 'en' ? 'Now' : '現在'}
        </button>
      </div>
      
      <div className="debugTunnelFee-status-group">
        <p className="debugTunnelFee-status-item">
          <span className="debugTunnelFee-status-label">{language === 'en' ? 'Current Simulated Datetime (HKT):' : '當前模擬日期時間（香港時間）：'}</span>
          <span className="debugTunnelFee-status-value">{formattedDebugDatetime}</span>
        </p>
      </div>

      <div className="debugTunnelFee-fees">
        <h4>{language === 'en' ? 'Current Tunnel Fees:' : '當前隧道收費：'}</h4>
        <div className="debugTunnelFee-grid">
          {renderDebugTunnelFees}
        </div>
      </div>
    </div>
  );
};

export default DebugTunnelFee;
